import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DialogFooter } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { InvoiceItemsList } from "@/components/InvoiceItems";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function InvoiceForm({ invoice, clients, onSave, onClose }) {
  const [form, setForm] = useState({
    client_id: invoice?.client_id || "",
    tax_rate: invoice?.tax_rate ?? 20,
    due_date: invoice?.due_date || "",
    notes: invoice?.notes || "",
    items: invoice?.items || [{ description: "", quantity: 1, unit_price: 0 }]
  });
  const [saving, setSaving] = useState(false);

  function handleItemChange(index, field, value) {
    setForm(function(prev) {
      var newItems = prev.items.slice();
      newItems[index] = Object.assign({}, newItems[index]);
      newItems[index][field] = field === "description" ? value : parseFloat(value) || 0;
      return Object.assign({}, prev, { items: newItems });
    });
  }

  function addItem() {
    setForm(function(prev) {
      return Object.assign({}, prev, { items: prev.items.concat([{ description: "", quantity: 1, unit_price: 0 }]) });
    });
  }

  function removeItem(index) {
    setForm(function(prev) {
      return Object.assign({}, prev, { items: prev.items.filter(function(_, i) { return i !== index; }) });
    });
  }

  var subtotal = form.items.reduce(function(sum, item) { return sum + (item.quantity * item.unit_price); }, 0);
  var taxAmount = subtotal * form.tax_rate / 100;
  var total = subtotal + taxAmount;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.client_id) { toast.error("Sélectionnez un client"); return; }
    if (form.items.length === 0 || !form.items[0].description) { toast.error("Ajoutez au moins une prestation"); return; }
    setSaving(true);
    try {
      var url = invoice?.invoice_id
        ? API_URL + "/api/invoices/" + invoice.invoice_id
        : API_URL + "/api/invoices";
      var method = invoice?.invoice_id ? "PUT" : "POST";
      var res = await fetch(url, {
        method: method, headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify(form)
      });
      if (res.ok) {
        toast.success(invoice?.invoice_id ? "Facture modifiée" : "Facture créée");
        onSave(); onClose();
      } else {
        var err = await res.json();
        toast.error(err.detail || "Erreur");
      }
    } catch (err) { toast.error("Erreur de connexion"); }
    setSaving(false);
  }

  // Build client options using createElement to avoid babel issues
  var clientOptions = [];
  for (var i = 0; i < clients.length; i++) {
    var c = clients[i];
    clientOptions.push(React.createElement(SelectItem, { key: c.client_id, value: c.client_id }, c.name));
  }

  return React.createElement("form", { onSubmit: handleSubmit, className: "space-y-4" },
    React.createElement("div", { className: "grid grid-cols-2 gap-4" },
      React.createElement("div", { className: "col-span-2" },
        React.createElement(Label, null, "Client"),
        React.createElement(Select, {
          value: form.client_id,
          onValueChange: function(v) { setForm(function(p) { return Object.assign({}, p, { client_id: v }); }); }
        },
          React.createElement(SelectTrigger, { "data-testid": "invoice-client-select" },
            React.createElement(SelectValue, { placeholder: "Sélectionner un client..." })
          ),
          React.createElement(SelectContent, null, clientOptions)
        )
      ),
      React.createElement("div", null,
        React.createElement(Label, null, "Taux TVA (%)"),
        React.createElement(Input, {
          type: "number", value: form.tax_rate, "data-testid": "invoice-tax-input",
          onChange: function(e) { setForm(function(p) { return Object.assign({}, p, { tax_rate: parseFloat(e.target.value) || 0 }); }); }
        })
      ),
      React.createElement("div", null,
        React.createElement(Label, null, "Date d'échéance"),
        React.createElement(Input, {
          type: "date", value: form.due_date, "data-testid": "invoice-due-date",
          onChange: function(e) { setForm(function(p) { return Object.assign({}, p, { due_date: e.target.value }); }); }
        })
      )
    ),
    React.createElement(InvoiceItemsList, {
      items: form.items,
      onItemChange: handleItemChange,
      onAddItem: addItem,
      onRemoveItem: removeItem
    }),
    React.createElement("div", { className: "text-right space-y-1" },
      React.createElement("p", { className: "text-sm text-muted-foreground" },
        "TVA (" + form.tax_rate + "%): ",
        React.createElement("span", { className: "font-medium text-foreground" }, taxAmount.toFixed(2) + " EUR")
      ),
      React.createElement("p", { className: "text-base font-semibold" }, "Total: " + total.toFixed(2) + " EUR")
    ),
    React.createElement("div", null,
      React.createElement(Label, null, "Notes"),
      React.createElement(Textarea, {
        value: form.notes, rows: 2, "data-testid": "invoice-notes",
        onChange: function(e) { setForm(function(p) { return Object.assign({}, p, { notes: e.target.value }); }); }
      })
    ),
    React.createElement(DialogFooter, null,
      React.createElement(Button, { type: "button", variant: "outline", onClick: onClose }, "Annuler"),
      React.createElement(Button, { type: "submit", disabled: saving, "data-testid": "save-invoice-btn" },
        saving ? React.createElement(Loader2, { className: "w-4 h-4 animate-spin mr-2" }) : null,
        invoice?.invoice_id ? "Modifier" : "Créer"
      )
    )
  );
}
